<template>
  <Teleport to="body">
    <div v-if="isOpen" class="modal-backdrop-custom" @click.self="$emit('close')">
      <div class="modal-dialog-custom">
        <form @submit.prevent="submitForm" class="modal-content-custom">
          
          <!-- Header -->
          <div class="modal-header-custom d-flex justify-content-between align-items-center">
            <div>
              <h5 class="modal-title mb-1">
                {{ mode === 'edit' ? currentLang.editTitle || currentLang.title : currentLang.title }}
              </h5>
              <p class="modal-subtitle mb-0">{{ currentLang.detail }}</p>
            </div>
            <button type="button" class="btn-close-modal" @click="$emit('close')">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body-custom">
            <ErrorAlert v-if="errorMessage" :message="errorMessage" class="mb-3" />

            <div class="row g-3">
              <div class="col-12 col-md-6">
                <label class="custom-label">
                  {{ currentLang.phone }}
                  <span class="required">*</span>
                </label>
                <input
                  type="text"
                  class="form-control custom-input"
                  :placeholder="currentLang.placeholderPhone"
                  v-model="form.phone"
                  required
                />
              </div>

              <div class="col-12 col-md-6">
                <label class="custom-label">
                  {{ currentLang.title }}
                  <span class="required">*</span>
                </label>
                <input
                  type="text"
                  class="form-control custom-input"
                  :placeholder="currentLang.placeholderTitle"
                  v-model="form.title"
                  required
                />
              </div>

              <div class="col-12">
                <label class="custom-label">
                  {{ currentLang.address }}
                  <span class="required">*</span>
                </label>
                <input
                  type="text"
                  class="form-control custom-input"
                  :placeholder="currentLang.placeholderAddress"
                  v-model="form.address"
                  required
                />
              </div>

              <div class="col-12">
                <label class="custom-label">
                  {{ currentLang.details }}
                  <span class="required">*</span>
                </label>
                <input
                  type="text"
                  class="form-control custom-input"
                  :placeholder="currentLang.placeholderDetails"
                  v-model="form.details"
                />
              </div>

              <div class="col-12">
                <label class="custom-label">
                  {{ currentLang.postalCode }}
                  <span class="required">*</span>
                </label>
                <input
                  type="text"
                  class="form-control custom-input"
                  :placeholder="currentLang.placeholderPostalCode"
                  v-model="form.postal_code"
                />
              </div>

              <!-- کشور -->
              <div class="col-12 col-md-4">
                <label class="custom-label">{{ currentLang.country }}</label>
                <input
                  class="form-control custom-input"
                  list="list-countries"
                  :value="countryName"
                  @input="handleInput('country', $event.target.value)"
                  @change="syncLocation('country')"
                />
                <datalist id="list-countries">
                  <option v-for="c in countries" :key="c.id" :value="c.name" />
                </datalist>
              </div>

              <!-- استان -->
              <div class="col-12 col-md-4">
                <label class="custom-label">{{ currentLang.province }}</label>
                <input
                  class="form-control custom-input"
                  list="list-provinces"
                  :value="provinceName"
                  @input="handleInput('province', $event.target.value)"
                  @change="syncLocation('province')"
                  :disabled="!form.country_id"
                />
                <datalist id="list-provinces">
                  <option v-for="p in provinces" :key="p.id" :value="p.name" />
                </datalist>
              </div>

              <!-- شهر -->
              <div class="col-12 col-md-4">
                <label class="custom-label">{{ currentLang.city }}</label>
                <input
                  class="form-control custom-input"
                  list="list-cities"
                  :value="cityName"
                  @input="handleInput('city', $event.target.value)"
                  @change="syncLocation('city')"
                  :disabled="!form.province_id"
                />
                <datalist id="list-cities">
                  <option v-for="c in cities" :key="c.id" :value="c.name" />
                </datalist>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer-custom d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-secondary cancel-btn" @click="$emit('close')">
              {{ currentLang.cancel || 'انصراف' }}
            </button>
            <button type="submit" class="btn save-btn" :disabled="loading">
              {{ loading ? currentLang.saving : currentLang.save }}
            </button>
          </div>

        </form>
      </div>
    </div>
  </Teleport>
</template>



<script setup>
import { ref, watch, computed } from "vue";
import api from "@/plugins/axios";
import { lang } from "@/plugins/lang";
import { alertStore } from "@/stores/alertStore";
import { showLoading, hideLoading } from "@/stores/loading";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import enText from "@/locales/en/addressPage";
import faText from "@/locales/fa/addressPage";

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  mode: { type: String, default: "create" },
  initialAddress: { type: Object, default: null },
  userId: { type: [String, Number], default: "" }
});

const emit = defineEmits(["close", "saved"]);

const translations = { en: enText, fa: faText };
const currentLang = computed(() => translations[lang.value]);

const loading = ref(false);
const errorMessage = ref("");

const countries = ref([]);
const provinces = ref([]);
const cities = ref([]);
const countryName = ref("");
const provinceName = ref("");
const cityName = ref("");
let timers = { country: null, province: null, city: null };

const initialForm = {
  title: "",
  phone: "",
  address: "",
  details: "",
  postal_code: "",
  country_id: "",
  province_id: "",
  city_id: "",
};

const form = ref({ ...initialForm });

const resetFormState = () => {
  form.value = { ...initialForm };
  countryName.value = "";
  provinceName.value = "";
  cityName.value = "";
  provinces.value = [];
  cities.value = [];
  errorMessage.value = "";
};

watch(
  () => props.isOpen,
  async (open) => {
    if (!open) return;
    onSearch("country", "");

    if (props.mode === "edit" && props.initialAddress) {
      const data = props.initialAddress;
      form.value = {
        title: data.title || "",
        phone: data.phone || "",
        address: data.address || "",
        details: data.details || "",
        postal_code: data.postal_code || "",
        country_id: data.country?.id || data.country_id || "",
        province_id: data.province?.id || data.province_id || "",
        city_id: data.city?.id || data.city_id || "",
      };

      countryName.value = data.country?.name || "";
      provinceName.value = data.province?.name || "";
      cityName.value = data.city?.name || "";

      if (form.value.country_id) await onSearch("province", "");
      if (form.value.province_id) await onSearch("city", "");
    } else {
      resetFormState();
    }
  },
  { immediate: true }
);

const submitForm = async () => {
  try {
    loading.value = true;
    showLoading();

    const payload = {
      ...form.value,
      user_id: props.userId,
    };

    const isEditMode = props.mode === "edit" && props.initialAddress?.id;
    const response = isEditMode
      ? await api.patch(`client/address/${props.initialAddress.id}`, payload)
      : await api.post("client/address", payload);

    if (response.status === 201 || response.status === 200) {
      alertStore.trigger(
        "success",
        currentLang.value.success,
        response?.data?.message || ""
      );
      emit("saved");
      emit("close");
    } else {
      errorMessage.value = response?.data?.message || "networkError";
    }
  } catch (error) {
    errorMessage.value = error.response?.data?.message || "error";
  } finally {
    loading.value = false;
    hideLoading();
  }
};

const onSearch = async (type, query) => {
  let url = "";
  if (type === "country") url = `general/location/countries?name=${query}`;
  if (type === "province")
    url = `general/location/provinces?country_id=${form.value.country_id}&name=${query}`;
  if (type === "city")
    url = `general/location/cities?province_id=${form.value.province_id}&name=${query}`;

  const res = await api.get(url);
  if (type === "country") countries.value = res.data.data;
  if (type === "province") provinces.value = res.data.data;
  if (type === "city") cities.value = res.data.data;
};

const handleInput = (type, val) => {
  if (type === "country") {
    countryName.value = val;
    form.value.country_id = "";
    resetLocations("country");
  }
  if (type === "province") {
    provinceName.value = val;
    form.value.province_id = "";
    resetLocations("province");
  }
  if (type === "city") {
    cityName.value = val;
    form.value.city_id = "";
  }

  clearTimeout(timers[type]);
  timers[type] = setTimeout(() => onSearch(type, val), 400);
};

const syncLocation = async (type) => {
  let list = [];
  if (type === "country") list = countries.value;
  if (type === "province") list = provinces.value;
  if (type === "city") list = cities.value;

  const searchTerm =
    type === "country"
      ? countryName.value
      : type === "province"
      ? provinceName.value
      : cityName.value;
  const item = list.find((i) => i.name === searchTerm);

  if (item) {
    if (type === "country") {
      form.value.country_id = item.id;
      await onSearch("province", "");
    }
    if (type === "province") {
      form.value.province_id = item.id;
      await onSearch("city", "");
    }
    if (type === "city") {
      form.value.city_id = item.id;
    }
  }
};

const resetLocations = (level) => {
  if (level === "country") {
    form.value.province_id = "";
    form.value.city_id = "";
    provinceName.value = "";
    cityName.value = "";
    provinces.value = [];
    cities.value = [];
  } else if (level === "province") {
    form.value.city_id = "";
    cityName.value = "";
    cities.value = [];
  }
};
</script>




<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}

.modal-dialog-custom {
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  display: flex;
}

.modal-content-custom {
  background: #ffffff;
  border-radius: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.modal-header-custom {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

.modal-subtitle {
  color: #64748b;
  font-size: 13px;
}

.btn-close-modal {
  background: transparent;
  border: none;
  font-size: 18px;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  transition: 0.2s;
}

.btn-close-modal:hover {
  color: #0f172a;
  background: #f1f5f9;
}

.modal-body-custom {
  padding: 24px;
  overflow-y: auto;
  max-height: calc(90vh - 160px);
}

.modal-footer-custom {
  padding: 16px 24px;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
}

.custom-label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.custom-input {
  height: 46px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  font-size: 14px;
  padding: 0 14px;
  transition: 0.2s ease;
  background: #fff;
}

.custom-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  outline: none;
}

.custom-input:disabled {
  background: #f1f5f9;
  cursor: not-allowed;
  opacity: 0.8;
}

.save-btn {
  height: 42px;
  padding: 0 20px;
  border-radius: 10px;
  background: #16a34a;
  color: white;
  font-weight: 600;
  border: none;
  transition: 0.2s;
}

.save-btn:hover {
  background: #15803d;
}

.cancel-btn {
  height: 42px;
  padding: 0 16px;
  border-radius: 10px;
  background: #e2e8f0;
  color: #475569;
  border: none;
  font-weight: 600;
}

.cancel-btn:hover {
  background: #cbd5e1;
}

.required {
  color: #ef4444;
  margin-right: 4px;
}
</style>
